<template>
	<ion-modal
		:is-open="isModalOpen"
		class="responsive-modal"
		@did-dismiss="closeModal"
		@ion-modal-did-present="initializeMap"
	>
		<ion-header>
			<ion-toolbar color="branding">
				<ion-buttons slot="end">
					<ion-button @click="closeModal">
						<ion-icon :icon="closeOutline" />
					</ion-button>
				</ion-buttons>
				<ion-title>Select Plantings</ion-title>
			</ion-toolbar>
		</ion-header>
		<ion-content>
			<div
				id="modal-map"
				ref="mapContainerRef"
				class="relative h-1/2 w-full"
			/>

			<MultiplePlantingsPopover
				:plantings="multiplePlantingsSelected"
				:reference-element="mapContainerRef"
				@done="() => (multiplePlantingsSelected = undefined)"
				@select="togglePlantingSelection"
			/>

			<div
				class="flex h-1/2 w-full flex-col border-t border-gray-200 bg-white"
			>
				<div
					class="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white p-2"
				>
					<ion-searchbar
						v-model="searchQuery"
						placeholder="Search plantings"
						class="!m-0"
						@ion-input="handleSearch"
					></ion-searchbar>

					<ion-button
						v-if="localSelectedPlantingIds.size > 0"
						fill="clear"
						size="small"
						@click="clearSelections"
					>
						Clear
					</ion-button>
				</div>

				<div class="flex-grow overflow-hidden">
					<RecycleScroller
						v-slot="{ item }"
						:items="filteredPlantings"
						key-field="id"
						class="h-full w-full overflow-y-auto"
						:item-size="60"
						:buffer="300"
					>
						<div
							:key="`planting-${item.id}-${localSelectedPlantingIds.has(item.id)}`"
							class="flex cursor-pointer items-center border-b border-gray-100 p-3"
							:class="{
								'bg-blue-50': localSelectedPlantingIds.has(
									item.id,
								),
							}"
							@click="togglePlantingSelection(item.id)"
						>
							<div class="mr-3 flex-shrink-0">
								<ion-checkbox
									:checked="
										localSelectedPlantingIds.has(item.id)
									"
									@ion-change="
										checkboxChanged($event, item.id)
									"
									@click.stop
								/>
							</div>
							<div class="flex-grow">
								<div class="font-medium text-gray-800">
									{{ item.name }}
								</div>
								<div class="text-xs text-gray-500">
									{{
										item.commodityName ||
										"Unknown commodity"
									}}
								</div>
							</div>
							<div class="flex-shrink-0">
								<span
									class="rounded-full px-2 py-1 text-xs font-medium"
									:class="{
										'bg-green-100 text-green-800':
											item.statusText === 'Active',
										'bg-gray-100 text-gray-800':
											item.statusText !== 'Active',
									}"
								>
									{{ item.statusText }}
								</span>
							</div>
						</div>
					</RecycleScroller>
				</div>
			</div>

			<div
				v-if="localSelectedPlantingIds.size > 0"
				class="absolute bottom-4 left-1/2 z-20 -translate-x-1/2 transform"
			>
				<button
					class="flex items-center rounded-full bg-blue-500 px-6 py-2 font-medium text-white shadow-lg hover:bg-blue-600"
					@click="completeAndClose"
				>
					<ion-icon slot="start" class="mr-2" :icon="checkmark" />
					Select {{ localSelectedPlantingIds.size }} Planting{{
						localSelectedPlantingIds.size !== 1 ? "s" : ""
					}}
				</button>
			</div>
		</ion-content>
	</ion-modal>
</template>

<script setup lang="ts">
	import { ref, watch, onUnmounted, nextTick, Ref, computed } from "vue";
	import {
		IonModal,
		IonHeader,
		IonToolbar,
		IonTitle,
		IonContent,
		IonButtons,
		IonButton,
		IonIcon,
		IonSearchbar,
		IonCheckbox,
	} from "@ionic/vue";
	import { RecycleScroller } from "vue-virtual-scroller";
	import { closeOutline, checkmark } from "ionicons/icons";
	import mapboxgl from "mapbox-gl";
	import { useDebounceFn, useTimeoutFn } from "@vueuse/core";
	import { useMyCustomer } from "seedgreen-shared/queries/customer";
	import usePlantingsLayer from "@/composables/map/usePlantingsLayer";
	import useMapboxControls from "@/composables/map/useMapboxControls";
	import MultiplePlantingsPopover from "@/views/MainMap/MultiplePlantingsPopover.vue";
	import { PlantingDTO } from "seedgreen-shared/dtos/PlantingDTO";
	import { captureException } from "@sentry/vue";
	import { useDefaultPlantingsQuery } from "@/composables/useDefaultPlantingsQuery";

	// Define the v-model using defineModel
	const selectedPlantingIds = defineModel<number[]>({
		default: () => [],
	});

	// Keep a local Set for more efficient operations
	const localSelectedPlantingIds = ref<Set<number>>(new Set());

	// Sync the model with our local Set
	watch(
		() => selectedPlantingIds.value,
		(newIds) => {
			// Update local Set when model changes externally
			localSelectedPlantingIds.value.clear();
			if (newIds && newIds.length > 0) {
				newIds.forEach((id) => {
					if (id !== null && id !== undefined) {
						localSelectedPlantingIds.value.add(id);
					}
				});
			}
		},
		{ immediate: true, deep: true },
	);

	// Map and UI state
	const map = ref<mapboxgl.Map | null>(null);
	const mapContainerRef = ref<HTMLElement | null>(null);
	const isModalOpen = ref(false);
	const isMapInitialized = ref(false);
	const layersInitialized = ref(false);
	const multiplePlantingsSelected = ref<PlantingDTO[]>();

	let plantingsLayerComposable: ReturnType<typeof usePlantingsLayer> | null =
		null;

	const searchQuery = ref("");
	const isSearching = ref(false);

	// Plantings data fetching
	const { plantings } = useDefaultPlantingsQuery();
	const plantingsArray = computed<PlantingDTO[]>(() => plantings.value ?? []);

	// Search functionality
	function handleSearch(event: CustomEvent) {
		isSearching.value = true;
		debouncedSearch(event.detail.value || "");
	}

	const debouncedSearch = useDebounceFn((value: string) => {
		searchQuery.value = value;
		isSearching.value = false;
	}, 250);

	// Filtered plantings based on search and selection status
	const filteredPlantings = computed(() => {
		if (!plantingsArray.value.length) return [];

		// Make a new array to avoid modifying the original
		let filtered = [...plantingsArray.value];

		if (searchQuery.value.trim()) {
			const query = searchQuery.value.toLowerCase();
			filtered = filtered.filter(
				(planting) =>
					(planting.name &&
						planting.name.toLowerCase().includes(query)) ||
					(planting.commodityName &&
						planting.commodityName.toLowerCase().includes(query)),
			);
		}

		// Sort the filtered results: selected plantings first, then the rest
		return filtered.sort((a, b) => {
			const aSelected = localSelectedPlantingIds.value.has(a.id);
			const bSelected = localSelectedPlantingIds.value.has(b.id);

			if (aSelected && !bSelected) return -1;
			if (!aSelected && bSelected) return 1;

			// Secondary sort by name to maintain stable order
			return (a.name ?? "").localeCompare(b.name ?? "");
		});
	});

	const { myCustomer } = useMyCustomer();

	// Handle clicking on plantings in the map
	function handleMapPlantingClick(plantingIds: number[]) {
		if (plantingIds.length === 1) {
			togglePlantingSelection(plantingIds[0]);
		} else if (plantingIds.length > 1) {
			if (plantingsLayerComposable) {
				plantingsLayerComposable.centerMapOnPlanting(plantingIds[0]);
			}
			multiplePlantingsSelected.value = plantingsArray.value?.filter(
				(p) => plantingIds.includes(p.id),
			);
		}
	}

	// Toggle selection of a planting
	function togglePlantingSelection(plantingId: number) {
		const isSelected = localSelectedPlantingIds.value.has(plantingId);

		// Update the local selection Set
		if (isSelected) {
			localSelectedPlantingIds.value.delete(plantingId);
		} else {
			localSelectedPlantingIds.value.add(plantingId);
		}

		// Update the model value
		selectedPlantingIds.value = Array.from(localSelectedPlantingIds.value);

		// Update map visualization - only if map and layers are ready
		if (map.value && layersInitialized.value) {
			// Check if source exists before updating feature state
			if (map.value.getSource("plantings-boundaries")) {
				// First remove old state
				map.value.removeFeatureState(
					{
						source: "plantings-boundaries",
						id: plantingId,
					},
					"selected",
				);

				// Then set new state
				const newState = !isSelected;
				map.value.setFeatureState(
					{
						source: "plantings-boundaries",
						id: plantingId,
					},
					{ selected: newState },
				);
			}
		}

		// Center the map on the selected planting
		if (plantingsLayerComposable && map.value && isMapInitialized.value) {
			plantingsLayerComposable.centerMapOnPlanting(plantingId);
		}
	}

	// Handle checkbox changes
	function checkboxChanged(event: CustomEvent, plantingId: number) {
		const isChecked = event.detail.checked;

		// Update the local selection Set
		if (isChecked) {
			localSelectedPlantingIds.value.add(plantingId);
		} else {
			localSelectedPlantingIds.value.delete(plantingId);
		}

		// Update the model value
		selectedPlantingIds.value = Array.from(localSelectedPlantingIds.value);

		// Update map visualization - only if map and layers are ready
		if (map.value && layersInitialized.value) {
			// Check if source exists before updating feature state
			if (map.value.getSource("plantings-boundaries")) {
				map.value.removeFeatureState(
					{
						source: "plantings-boundaries",
						id: plantingId,
					},
					"selected",
				);

				map.value.setFeatureState(
					{
						source: "plantings-boundaries",
						id: plantingId,
					},
					{ selected: isChecked },
				);
			}
		}
	}

	// Clear all selections
	function clearSelections() {
		if (map.value && layersInitialized.value) {
			// Check if source exists before updating feature states
			if (map.value.getSource("plantings-boundaries")) {
				Array.from(localSelectedPlantingIds.value).forEach((id) => {
					map.value?.removeFeatureState(
						{
							source: "plantings-boundaries",
							id,
						},
						"selected",
					);

					map.value?.setFeatureState(
						{
							source: "plantings-boundaries",
							id,
						},
						{ selected: false },
					);
				});
			}
		}

		localSelectedPlantingIds.value.clear();
		selectedPlantingIds.value = [];
	}

	// Complete selection and close modal
	function completeAndClose() {
		// Update the model one final time to ensure it's in sync
		selectedPlantingIds.value = Array.from(localSelectedPlantingIds.value);
		closeModal();
	}

	// Initialize the map
	const initializeMap = async () => {
		await nextTick();

		if (!mapContainerRef.value) {
			console.error("Map container element not found.");
			return;
		}

		if (map.value) {
			map.value.remove();
			map.value = null;
		}

		// Reset all flags and references
		isMapInitialized.value = false;
		layersInitialized.value = false;
		plantingsLayerComposable = null;

		try {
			// default center for the USA if no customer map center is set
			const USA: [number, number] = [-95, 38];

			// Determine initial center and zoom
			let initialCenter: [number, number] = USA;
			const initialZoom = 2;

			if (myCustomer.value?.mapCenter) {
				initialCenter = myCustomer.value.mapCenter;
			}

			map.value = new mapboxgl.Map({
				container: mapContainerRef.value,
				style: "mapbox://styles/mapbox/streets-v11", // Add explicit style URL
				center: initialCenter,
				zoom: initialZoom,
			});

			useMapboxControls(map as Ref<mapboxgl.Map | null>);

			// Use once to ensure it only fires once per map instance
			// @ts-expect-error TS2589: Type instantiation is excessively deep and possibly infinite.
			map.value.once("load", () => {
				if (!map.value) return;

				map.value.resize();

				// Create fresh composable instance for this map
				plantingsLayerComposable = usePlantingsLayer(
					map as Ref<mapboxgl.Map | null>,
					plantingsArray,
					handleMapPlantingClick,
				);

				// Initialize layers with fresh composable
				plantingsLayerComposable.initPlantingsSources();
				plantingsLayerComposable.initPlantingsLayers();
				plantingsLayerComposable.setPlantingsLayerMode("foreground");

				// Set flag after initialization is complete
				layersInitialized.value = true;

				// Add a small delay to ensure sources are fully loaded
				setTimeout(() => {
					// Center map appropriately
					if (localSelectedPlantingIds.value.size > 0) {
						plantingsLayerComposable?.centerMapOnPlanting(
							Array.from(localSelectedPlantingIds.value)[0],
						);
					} else if (plantingsArray.value?.length) {
						plantingsLayerComposable?.centerMapOnPlantings();
					}

					// First clear any existing states
					plantingsArray.value.forEach((planting) => {
						map.value?.removeFeatureState(
							{
								source: "plantings-boundaries",
								id: planting.id,
							},
							"selected",
						);
					});

					// Then set feature states for initially selected plantings
					localSelectedPlantingIds.value.forEach((id) => {
						map.value?.setFeatureState(
							{
								source: "plantings-boundaries",
								id,
							},
							{ selected: true },
						);
					});

					isMapInitialized.value = true;
				}, 200); // Small delay to ensure sources are ready
			});
		} catch (error) {
			console.error("Error initializing map:", error);
			captureException(error);
		}
	};

	// Resize the map
	function resizeMap() {
		if (map.value) {
			map.value.resize();
		}
	}

	// Open the modal and initialize selections
	const openModal = () => {
		// Initialize selections from the current model value
		localSelectedPlantingIds.value.clear();

		if (selectedPlantingIds.value && selectedPlantingIds.value.length > 0) {
			selectedPlantingIds.value.forEach((id) => {
				if (id !== null && id !== undefined) {
					localSelectedPlantingIds.value.add(id);
				}
			});
		}

		isModalOpen.value = true;
		searchQuery.value = "";
	};

	// Clean up resources
	const cleanupResources = () => {
		if (map.value) {
			try {
				map.value.remove();
				map.value = null;

				// Reset all flags and references
				isMapInitialized.value = false;
				layersInitialized.value = false;
				plantingsLayerComposable = null;
			} catch (err) {
				console.warn("Error cleaning up map resources:", err);
			}
		}

		multiplePlantingsSelected.value = undefined;
		searchQuery.value = "";
	};

	// Close the modal
	const closeModal = () => {
		isModalOpen.value = false;
		cleanupResources();
	};

	// Cleanup on unmount
	onUnmounted(cleanupResources);

	// Resize map when modal opens
	watch(isModalOpen, (isOpen) => {
		if (isOpen) {
			useTimeoutFn(resizeMap, 300);
		}
	});

	// Expose public methods
	defineExpose({
		openModal,
		closeModal,
	});
</script>

<style scoped>
	/* Desktop styles */
	@media (min-width: 768px) {
		.responsive-modal {
			--height: 90%;
		}
	}
</style>
